import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAuditLog } from '@/hooks/useAuditLog';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type ScopeType = 'global' | 'team' | 'personal';

function buildScopeFilter(scope: ScopeType, scopeId?: string, projectId?: string): string {
  const conditions: string[] = [];
  if (projectId) conditions.push(`project_id.eq.${projectId}`);
  conditions.push(`and(scope_type.eq.global,project_id.is.null)`);
  if (scope === 'team' && scopeId) conditions.push(`and(scope_type.eq.team,scope_id.eq.${scopeId},project_id.is.null)`);
  if (scope === 'personal') conditions.push(`and(scope_type.eq.personal,project_id.is.null)`);
  return conditions.join(',');
}

export const useAgents = (scope: ScopeType, scopeId?: string, projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['agents', scope, scopeId, projectId],
    enabled: !!user,
    queryFn: async () => {
      const filter = buildScopeFilter(scope, scopeId, projectId);
      const { data, error } = await supabase.from('agents').select('*').or(filter).order('updated_at', { ascending: false });
      if (error) throw error;

      const { data: permissions, error: permissionsError } = await supabase
        .from('agent_permissions')
        .select('agent_id')
        .eq('user_id', user!.id);
      if (permissionsError) throw permissionsError;

      const sharedIds = (permissions ?? []).map(permission => permission.agent_id);
      if (sharedIds.length === 0) return data ?? [];

      const { data: shared, error: sharedError } = await supabase
        .from('agents')
        .select('*')
        .in('id', sharedIds);
      if (sharedError) throw sharedError;

      const map = new Map<string, NonNullable<typeof data>[number]>();
      (data ?? []).forEach(agent => map.set(agent.id, agent));
      (shared ?? []).forEach(agent => map.set(agent.id, agent));
      return Array.from(map.values()).sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    },
  });
};

export const useCreateAgent = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (agent: Omit<TablesInsert<'agents'>, 'owner_id'>) => {
      const { data, error } = await supabase.from('agents').insert({ ...agent, owner_id: user!.id }).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      logAction({ action: 'create_agent', targetType: 'agent', targetId: data.id, metadata: { name: data.name } });
    },
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async ({ id, ...updates }: TablesUpdate<'agents'> & { id: string }) => {
      const { data, error } = await supabase.from('agents').update(updates).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      logAction({ action: 'update_agent', targetType: 'agent', targetId: data.id });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('agents').delete().eq('id', id).select('id').single();
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      logAction({ action: 'delete_agent', targetType: 'agent', targetId: id });
    },
  });
};
